import { z } from 'zod'
import { HugeiconsIcon } from '@hugeicons/react'
import { UserGroupIcon } from '@hugeicons/core-free-icons'
import { useConvexChangeUserManager } from '../services/users.convex'
import { useMyConvexOrganizationId } from '@/features/auth'
import { useOrganizationManagers } from '@/features/organization/services/organization.convex'
import type { AnyFieldApi } from '@tanstack/react-form'
import { FieldInfo } from '@/components/common/field-info'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFormDialog } from '@/contexts/form-dialog-context'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'

interface ChangeManagerButtonProps {
  userId: string
  userName: string
  currentManagerId?: string
  children?: React.ReactNode
  onSuccess?: () => void
}

interface ChangeManagerFormData {
  managerId: string
}

const CHANGE_MANAGER_SCHEMA = z.object({
  managerId: z.string().min(1, 'Manager is required'),
})

export const ChangeManagerButton = ({
  userId,
  userName,
  currentManagerId,
  children,
  onSuccess,
}: ChangeManagerButtonProps) => {
  const { showFormDialog } = useFormDialog()
  const { showConfirmDialog } = useConfirmDialog()
  const convexOrgId = useMyConvexOrganizationId()

  // Fetch available managers
  const { data: managers, isLoading: isLoadingManagers } =
    useOrganizationManagers(convexOrgId)

  // Mutation for changing manager
  const changeManagerMutation = useConvexChangeUserManager()

  const handleSubmit = async (data: ChangeManagerFormData) => {
    // Check if manager actually changed
    if (data.managerId === currentManagerId) {
      return Promise.resolve()
    }

    // Get the new manager name for confirmation message
    const newManager = managers?.find((m) => m.id === data.managerId)
    const newManagerName = newManager?.name || 'Unknown'

    // Show confirmation dialog
    showConfirmDialog({
      title: 'Confirm Manager Change',
      description: `Are you sure you want to change ${userName}'s manager to ${newManagerName}?`,
      confirmText: 'Change Manager',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        await changeManagerMutation.mutateAsync({
          userId,
          managerId: data.managerId,
        })
        onSuccess?.()
      },
    })

    return Promise.resolve()
  }

  const handleChangeManagerClick = () => {
    if (isLoadingManagers) {
      return
    }

    showFormDialog<ChangeManagerFormData, void>({
      title: `Change Manager for ${userName}`,
      description: 'Select a new manager for this user',
      submitText: 'Update Manager',
      showSuccessState: false,
      formOptions: {
        defaultValues: {
          managerId: currentManagerId || '',
        },
      },
      children: (form) => {
        return (
          <>
            {/* Manager Selection */}
            <form.Field
              name="managerId"
              validators={{
                onChange: CHANGE_MANAGER_SCHEMA.shape.managerId,
              }}
            >
              {(field: AnyFieldApi) => (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Select Manager</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={isLoadingManagers}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {managers && managers.length > 0 ? (
                        managers.map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>
                            {manager.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="text-sm text-muted-foreground p-2">
                          No managers available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FieldInfo field={field} />
                </div>
              )}
            </form.Field>
          </>
        )
      },
      onSubmit: handleSubmit,
      onSuccess,
    })
  }

  return (
    <Button
      onClick={handleChangeManagerClick}
      variant="outline"
      size="sm"
      disabled={isLoadingManagers || changeManagerMutation.isPending}
    >
      {children || (
        <>
          <HugeiconsIcon icon={UserGroupIcon} className="h-4 w-4 mr-2" />
          Change Manager
        </>
      )}
    </Button>
  )
}
